import { queryOptions } from "@tanstack/react-query";
import type { WorkHoursService } from "@/services/work-hours.service";
import type { WorkspaceAvailabilityService } from "@/services/workspace-availability.service";
import { getQueryClient } from "./client";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The work-hours reads (NG-2814):
 *
 * - `workHours.mine`: the signed-in user's own work hours (weekly hours, the
 *   day's start and end, time zone, capacity), `['*', uid, 'work-hours',
 *   'mine']`. No workspace owns it, so it sits under `GLOBAL_TENANT`. Mutable:
 *   the profile Work Hours page edits it, and the Timeline and Events pages
 *   draw their hour window from it.
 * - `availability.team`: a workspace's members' availability over a date
 *   range, as the Availability page shows it,
 *   `[slug, uid, 'availability', 'team', {start, end}]`. Mutable.
 *
 * Both are plain GETs, so both declare `"safe-read"`.
 *
 * @writers `WorkHoursStore.updateWorkHours` patches the user's entry at once,
 * puts it back on failure and replaces it with the server's answer. Nothing in
 * the app writes team availability; it is read only.
 */

/** The signed-in user's own work hours key; `scope` is under `GLOBAL_TENANT`. */
export function myWorkHoursKey(scope: QueryScope) {
  return scopedKey(scope, "work-hours", "mine");
}

/** The signed-in user's own work hours. */
export function myWorkHoursQuery(
  service: Pick<WorkHoursService, "getCurrentUserWorkHours">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: myWorkHoursKey(scope),
    queryFn: scopedRead((request) => service.getCurrentUserWorkHours(request), {
      retry: "safe-read",
    }),
  });
}

/** A workspace's team availability over `start`..`end` (ISO dates). */
export function teamAvailabilityQuery(
  service: Pick<WorkspaceAvailabilityService, "getTeamAvailability">,
  scope: QueryScope,
  range: { start: string; end: string },
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, "availability", "team", { start: range.start, end: range.end }),
    queryFn: scopedRead(
      (request) =>
        service.getTeamAvailability(scope.tenant, range.start, range.end, undefined, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * Warm a workspace's team availability for `range` from the Availability
 * loader. Before the session resolves there is nothing to warm, and the
 * page's own read asks once it does.
 */
export async function prefetchTeamAvailability(
  service: Pick<WorkspaceAvailabilityService, "getTeamAvailability">,
  slug: string,
  range: { start: string; end: string },
): Promise<void> {
  const scope = scopeIfReady(slug, "team availability");
  if (!scope) return;
  await getQueryClient().fetchQuery(teamAvailabilityQuery(service, scope, range));
}
